<template>
  <div class="login-page">
    <main class="login-main">
      <section class="login-panel" aria-labelledby="login-title">
        <div class="panel-decorations" aria-hidden="true">
          <div class="decor-glow decor-glow--top"></div>
          <div class="decor-glow decor-glow--bottom"></div>
          <CIcon name="cil-paw" class="decor-paw" />
        </div>
        <div class="login-content">
          <div class="brand-lockup" aria-label="Save Animals">
            <div class="brand-mark" aria-hidden="true">
              <img src="/img/app_icon.png" alt="" />
            </div>
            <div class="brand-copy">
              <div class="brand-name">Save Animals</div>
            </div>
          </div>

          <form class="login-form" @submit.prevent="login" novalidate>
            <div class="heading-block">
              <h1 id="login-title">Welcome <span>Back!</span></h1>
              <p>Sign in to continue your wildlife rescue journey</p>
            </div>

            <div class="field-group">
              <label class="sr-only" for="login-phone">Username / Mobile Number</label>
              <span class="field-icon" aria-hidden="true"><CIcon name="cil-user" /></span>
              <input
                id="login-phone"
                v-model="model.phone"
                type="text"
                inputmode="email"
                autocomplete="username"
                placeholder="Username / Mobile Number"
              >
            </div>

            <div class="field-group">
              <label class="sr-only" for="login-password">Password</label>
              <span class="field-icon" aria-hidden="true"><CIcon name="cil-lock-locked" /></span>
              <input
                id="login-password"
                v-model="model.password"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="current-password"
                placeholder="Password"
              >
              <button
                class="password-toggle"
                type="button"
                :aria-label="showPassword ? 'Hide password' : 'Show password'"
                :aria-pressed="showPassword.toString()"
                @click="showPassword = !showPassword"
              >
                <span class="eye-icon" :class="{ 'is-hidden': !showPassword }" aria-hidden="true"></span>
              </button>
            </div>


            <div
              v-if="error.msg"
              class="login-alert"
              :class="`login-alert--${error.color}`"
              role="alert"
            >
              {{ error.msg }}
            </div>

            <button class="primary-action" type="submit" :disabled="loader">
              <CSpinner v-if="loader" size="sm" color="light" aria-label="Signing in" />
              <span v-else>Sign In</span>
            </button>

            <div class="divider" aria-hidden="true"><span>or</span></div>

            <div class="app-download-section">
              <span class="app-download-label">Download app from</span>
              <div class="app-download-icons">
                <a href="https://apps.apple.com/in/app/saveanimals/id6786569983" target="_blank" rel="noopener noreferrer" class="app-icon-btn" aria-label="App Store">
                  <CIcon name="cib-apple" />
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.leopardtechlabz.saveanimals" target="_blank" rel="noopener noreferrer" class="app-icon-btn" aria-label="Play Store">
                  <CIcon name="cib-android" />
                </a>
              </div>
            </div>


            <nav class="policy-links" aria-label="Legal and safety information">
              <router-link to="/privacy-policy">Privacy Policy</router-link>
              <span class="policy-separator" aria-hidden="true">•</span>
              <router-link to="/child-safety-standards">Child Safety Standards</router-link>
            </nav>
          </form>
          
          <div class="conservation-quote">
            <svg class="quote-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.036c0 1.027 1 2 2 2z"></path>
              <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 1 1 2 1z"></path>
            </svg>
            <p>"The clearest way into the Universe is through a forest wilderness."</p>
            <span>— John Muir</span>
          </div>

          <div class="panel-footer">
            © 2026 Save Animals. All rights reserved.
          </div>
        </div>
      </section>

      <section class="hero-panel" aria-label="Wildlife rescue partnership">
        <!-- S-curve divider between white panel and hero image -->
        <svg class="hero-curve" xmlns="http://www.w3.org/2000/svg"
             viewBox="0 0 100 1000" preserveAspectRatio="none" aria-hidden="true">
          <!-- 1. Light green stripe -->
          <path d="M100,0 C100,150 15,350 15,500 C15,650 100,850 100,1000"
                fill="none" stroke="#c2dcbc" stroke-width="20" stroke-linecap="round"/>
          <!-- 2. White S-curve fill -->
          <path d="M0,0 L100,0 C100,150 15,350 15,500 C15,650 100,850 100,1000 L0,1000 Z"
                fill="white"/>
          <!-- 3. Dark green accent line -->
          <path d="M100,0 C100,150 15,350 15,500 C15,650 100,850 100,1000"
                fill="none" stroke="#2e6b3e" stroke-width="3" transform="translate(9, 0)"/>
        </svg>
        <img class="hero-photo" src="/img/login-rescue-hero-v2.png" alt="Wildlife rescuer caring for a spotted deer beside a forest lake">
      </section>
    </main>
  </div>
</template>

<script>
import axios from 'axios';
import store from '../../store';

export default {
  name: 'Login',
  data() {
    return {
      baseURL: store.state.baseURL,
      model: {
        phone: '',
        password: '',
        deviceId: '',
        deviceType: '',
        lat: '',
        lng: ''
      },
      error: {
        color: '',
        msg: '',
        counter: 0
      },
      loader: false,
      showPassword: false,
      rememberMe: false
    }
  },
  created() {
    this.model.deviceType = this.getOS() + " - " + this.getBrowser();
    this.model.deviceId = this.getUniqueId();
  },
  methods: {
    forgotPassword() {
      this.error = {
        color: 'info',
        msg: 'Please use your App to recover your password.',
        counter: 3
      }
    },
    login() {
      this.loader = true;

      if (this.model.phone.trim() === '' || this.model.password === '') {
        this.error = {
          color: 'danger',
          msg: 'Email/Password cannot be empty!',
          counter: 3
        }
        setTimeout(() => {
          this.loader = false;
        }, 1000);
      } else {
        axios({
          url: this.baseURL + "admin/login",
          method: "POST",
          data: this.model
        })
        .then(response => {
          if (response.data.status) {
            this.error = {
              color: 'success',
              msg: 'Logging in..',
              counter: 5
            }
            setTimeout(() => {
              store.commit('loginUser', {
                token: response.data.data.token,
                user: response.data.data
              });
              this.$router.push({
                name: 'Dashboard'
              });
            }, 2000);
          } else {
            setTimeout(() => {
              this.loader = false;
            }, 1500);
            this.error = {
              color: 'danger',
              msg: response.data.message + ' - ' + response.data.error,
              counter: 3
            }
          }
        })
        .catch(err => {
          setTimeout(() => {
            this.loader = false;
          }, 1500);
          this.error = {
            color: 'danger',
            msg: err.response !== undefined ? (err.response.data.message + '\n' + err.response.data.error) : err,
            counter: 5
          }
        })
      }
    },
    getOS() {
      var userAgent = window.navigator.userAgent,
          platform = window.navigator.platform,
          macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'],
          windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'],
          iosPlatforms = ['iPhone', 'iPad', 'iPod'],
          os = null;

      if (macosPlatforms.indexOf(platform) !== -1) {
        os = 'Mac OS';
      } else if (iosPlatforms.indexOf(platform) !== -1) {
        os = 'iOS';
      } else if (windowsPlatforms.indexOf(platform) !== -1) {
        os = 'Windows';
      } else if (/Android/.test(userAgent)) {
        os = 'Android';
      } else if (!os && /Linux/.test(platform)) {
        os = 'Linux';
      }

      return os;
    },
    getBrowser() {
      if ((navigator.userAgent.indexOf("Opera") || navigator.userAgent.indexOf('OPR')) !== -1) {
        return 'Opera';
      } else if (navigator.userAgent.indexOf("Chrome") !== -1) {
        return 'Chrome';
      } else if (navigator.userAgent.indexOf("Safari") !== -1) {
        return 'Safari';
      } else if (navigator.userAgent.indexOf("Firefox") !== -1) {
        return 'Firefox';
      } else if ((navigator.userAgent.indexOf("MSIE") !== -1) || (document.documentMode === true)) {
        return 'IE';
      }
      return 'Unknown Browser';
    },
    getUniqueId() {
      var navigator_info = window.navigator;
      var screen_info = window.screen;
      var uid = navigator_info.mimeTypes.length;
      uid += navigator_info.userAgent.replace(/\D+/g, '');
      uid += navigator_info.plugins.length;
      uid += screen_info.height || '';
      uid += screen_info.width || '';
      uid += screen_info.pixelDepth || '';
      return uid;
    }
  }
}
</script>


<style scoped>
/* ─── Design Tokens ─────────────────────────────────────── */
.login-page {
  --green:       #2e6b3e;
  --green-dark:  #1e4d2b;
  --green-light: #f0f7f2;
  --navy:        #10213b;
  --muted:       #718096;
  --border:      #d9dfe7;
  font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8faf8;
  color: var(--navy);
  overflow-x: hidden;
}

.login-page *,
.login-page *::before,
.login-page *::after { box-sizing: border-box; }

/* ─── Two-column grid ────────────────────────────────────── */
.login-main {
  flex: 1 0 auto;
  min-height: 100vh;
  display: grid;
  grid-template-columns: 480px 1fr;
}

/* ─── Left white panel ───────────────────────────────────── */
.login-panel {
  position: relative;
  z-index: 4;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  background: #fff;
  overflow: hidden;
}

/* ─── Panel Decorations ──────────────────────────────────── */
.panel-decorations {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 1;
}

.decor-glow {
  position: absolute;
  width: 350px;
  height: 350px;
  border-radius: 50%;
  filter: blur(70px);
  opacity: 0.6;
}

.decor-glow--top {
  top: -120px;
  right: -120px;
  background: var(--green-light);
}

.decor-glow--bottom {
  bottom: -120px;
  left: -50px;
  background: #e1efe5;
}

.decor-paw {
  position: absolute;
  bottom: -80px;
  right: -80px;
  width: 450px;
  height: 450px;
  color: var(--green);
  opacity: 0.08;
  transform: rotate(20deg);
}

.login-content {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  max-width: 440px;
  height: 100%;
  margin: 0 auto;
  padding: 40px 32px 30px 32px;
}

/* ─── Brand lockup ───────────────────────────────────────── */
.brand-lockup {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 52px;
}

.brand-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--green);
}

.brand-mark img {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.brand-name {
  font-size: 17px;
  font-weight: 800;
  color: var(--navy);
  letter-spacing: -0.3px;
  white-space: nowrap;
}

/* ─── Heading ────────────────────────────────────────────── */
.heading-block {
  margin-bottom: 32px;
}

.heading-block h1 {
  margin: 0 0 8px;
  font-size: clamp(30px, 2.5vw, 34px);
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -1px;
  color: var(--navy);
}

.heading-block h1 span {
  color: var(--green);
}

.heading-block p {
  margin: 0;
  font-size: 15px;
  color: var(--muted);
}

/* ─── Screen-reader only ─────────────────────────────────── */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0,0,0,0);
  white-space: nowrap;
  border: 0;
}

/* ─── Input fields ───────────────────────────────────────── */
.field-group {
  position: relative;
  height: 56px;
  margin-bottom: 16px;
  width: 100%;
}

.field-group input {
  width: 100%;
  height: 100%;
  padding: 0 55px 0 50px;
  font: inherit;
  font-size: 15px;
  color: #2e3540;
  border: 1.5px solid var(--border);
  border-radius: 10px;
  outline: none;
  background: #fff;
  transition: border-color .2s ease, box-shadow .2s ease;
}

.field-group input::placeholder {
  color: #a0aab4;
  opacity: 1;
}

.field-group input:focus {
  border-color: var(--green);
  box-shadow: 0 0 0 3px rgba(46, 107, 62, .12);
}

.field-icon {
  position: absolute;
  z-index: 1;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  display: grid;
  place-items: center;
  color: #a0aab4;
}

.field-icon .c-icon {
  width: 20px;
  height: 20px;
}

/* ─── Password toggle ────────────────────────────────────── */
.password-toggle {
  position: absolute;
  top: 0;
  right: 0;
  width: 50px;
  height: 56px;
  display: grid;
  place-items: center;
  border: 0;
  background: transparent;
  color: #a0aab4;
  cursor: pointer;
}

.password-toggle:focus-visible {
  outline: 2px solid var(--green);
  outline-offset: -8px;
  border-radius: 10px;
}

.eye-icon {
  position: relative;
  width: 20px;
  height: 13px;
  border: 2px solid currentColor;
  border-radius: 50% / 60%;
}

.eye-icon::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: currentColor;
  transform: translate(-50%, -50%);
}

.eye-icon.is-hidden::before {
  content: '';
  position: absolute;
  z-index: 1;
  width: 24px;
  height: 2px;
  top: 4px;
  left: -3px;
  background: currentColor;
  transform: rotate(-35deg);
}

/* ─── Alert ──────────────────────────────────────────────── */
.login-alert {
  margin: 0 0 12px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.4;
  white-space: pre-line;
}

.login-alert--danger  { color: #842029; background: #f8d7da; border: 1px solid #f5c2c7; }
.login-alert--success { color: #0f5132; background: #d1e7dd; border: 1px solid #badbcc; }
.login-alert--info    { color: #055160; background: #cff4fc; border: 1px solid #b6effb; }

/* ─── Buttons ────────────────────────────────────────────── */
.primary-action {
  width: 100%;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: none;
  border-radius: 10px;
  background: var(--green);
  color: #fff;
  font: inherit;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
  transition: background .18s, transform .15s, box-shadow .18s;
  box-shadow: 0 4px 14px rgba(46, 107, 62, .30);
}

.primary-action:hover:not(:disabled) {
  background: var(--green-dark);
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(46, 107, 62, .36);
}

.primary-action:disabled {
  cursor: wait;
  opacity: .75;
}

.primary-action .c-icon { width: 22px; height: 22px; }

/* ─── Divider ────────────────────────────────────────────── */
.divider {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 18px 0 16px;
  color: #b0bac8;
  font-size: 13px;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--border);
}

/* ─── App Download ───────────────────────────────────────── */
.app-download-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 10px 0;
}

.app-download-label {
  font-size: 14px;
  color: var(--muted);
  font-weight: 500;
}

.app-download-icons {
  display: flex;
  gap: 12px;
}

.app-icon-btn {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border: 1.5px solid var(--border);
  border-radius: 50%;
  background: #fff;
  color: var(--navy);
  text-decoration: none;
  cursor: pointer;
  transition: all .2s ease;
}

.app-icon-btn:hover {
  background: var(--green-light);
  border-color: var(--green);
  color: var(--green);
  transform: translateY(-2px);
}

.app-icon-btn .c-icon { 
  width: 22px; 
  height: 22px; 
}

.download-notice {
  margin: 8px 0 0;
  font-size: 12px;
  color: var(--muted);
  text-align: center;
}

/* ─── Policy links ───────────────────────────────────────── */
.policy-links {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
}

.policy-links a {
  color: var(--green);
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: opacity .15s;
}

.policy-links a:hover {
  opacity: .75;
  text-decoration: underline;
}

.policy-separator {
  color: var(--muted);
  font-size: 12px;
  font-weight: 700;
}

/* ─── Panel footer ───────────────────────────────────────── */
.login-form {
  margin-top: 30px;
  margin-bottom: auto;
}

.conservation-quote {
  margin: 40px auto;
  text-align: center;
  max-width: 320px;
}

.quote-icon {
  width: 28px;
  height: 28px;
  color: #a4c9af;
  margin-bottom: 12px;
  opacity: 0.5;
}

.conservation-quote p {
  font-size: 15px;
  line-height: 1.5;
  color: var(--muted);
  font-style: italic;
  margin: 0 0 10px;
}

.conservation-quote span {
  font-size: 13px;
  font-weight: 600;
  color: var(--green);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.panel-footer {
  font-size: 12px;
  color: #aab0bb;
  padding-top: 20px;
  border-top: 1px solid var(--border);
  text-align: center;
}

/* ─── Hero / Right panel ─────────────────────────────────── */
.hero-panel {
  position: relative;
  min-width: 0;
  overflow: hidden;
  background: #c8d8c2;
}

/* The S-curve wave SVG divider */
.hero-curve {
  position: absolute;
  z-index: 4;
  top: 0;
  left: 0;
  width: 100px;
  height: 100%;
  pointer-events: none;
  overflow: visible;
}

.hero-photo {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  object-position: 50% 50%;
}

/* ─── Partner logos block — centered in hero panel ───────── */
.top-right-partners {
  position: absolute;
  z-index: 5;
  top: clamp(60px, 12vh, 130px);
  left: 54%;
  width: min(78%, 700px);
  transform: translateX(-48%);
  text-align: center;
}

/* Radial white glow behind logos (no solid card) */
.top-right-partners::before {
  content: '';
  position: absolute;
  z-index: -1;
  inset: -24px -40px -12px;
  background: radial-gradient(ellipse at center, rgba(255,255,255,.72) 0%, rgba(255,255,255,.38) 55%, rgba(255,255,255,0) 80%);
  pointer-events: none;
}

.partner-logos {
  height: clamp(80px, 9vh, 114px);
  display: grid;
  grid-template-columns: 1fr 1px 1.15fr 1px 1.55fr;
  align-items: center;
  gap: clamp(16px, 2vw, 36px);
}

.partner-logo {
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.partner-logo img {
  max-width: 100%;
  max-height: 100%;
  display: block;
  object-fit: contain;
}

.partner-logo--ifaw img  { mix-blend-mode: multiply; filter: contrast(1.05); }
.partner-logo--wti img   { max-height: 112%; }
.partner-logo--leopard img { width: 100%; }

.logo-separator {
  width: 1px;
  height: 100%;
  background: rgba(35, 49, 57, .32);
  flex-shrink: 0;
}

.tagline-text {
  margin: 22px 0 12px;
  color: #3e6e50;
  font-size: clamp(16px, 1.3vw, 24px);
  font-style: italic;
  font-weight: 500;
  text-align: center;
  text-shadow: 0 1px 2px rgba(255,255,255,.85);
}

.botanical-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #6aaa7a;
  width: 100%;
}

.botanical-divider span {
  height: 1.5px;
  width: 70px;
  background: #86ae6b;
  border-radius: 2px;
}

.botanical-divider svg {
  color: #78a953;
  width: 22px;
  height: 22px;
}

/* ─── Responsive ─────────────────────────────────────────── */
@media (max-width: 1100px) {
  .login-main { grid-template-columns: 390px 1fr; }
  .login-content { padding: 32px 36px 28px; }
}

@media (max-width: 860px) {
  .login-main {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
  }
  .login-panel { order: 1; }
  .login-content { padding: 32px 28px 28px; max-width: 480px; margin: 0 auto; width: 100%; }
  .hero-panel {
    order: 2;
    min-height: 400px;
    flex-shrink: 0;
  }
  /* Hide S-curve on mobile — full-width stacked layout */
  .hero-curve { display: none; }
  .top-right-partners { top: 24px; left: 50%; width: min(90%, 600px); transform: translateX(-50%); }
  .partner-logos { height: clamp(60px, 8vh, 90px); }
}

@media (max-width: 540px) {
  .login-content { padding: 24px 20px 20px; }
  .brand-lockup { margin-bottom: 36px; }
  .heading-block h1 { font-size: 30px; }
  .field-group { height: 50px; }
  .password-toggle { height: 50px; }
  .primary-action, .download-action { height: 48px; font-size: 15px; }
  .hero-panel { min-height: 280px; }
  .partner-logos { flex-wrap: wrap; justify-content: center; gap: 4px; }
  .logo-separator { display: none; }
  .partner-logo { height: 26px; }
}
</style>
